import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import "./TeamReimbursements.scss";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import IconButton from "../../components/IconButton/IconButton";
import GeneralTable from "../../components/Table/GeneralTable";
import { toast } from "react-toastify";

function TeamReimbursements({ onRequestProcessed }) {
  const { user } = useAuth();

  const [reimbursements, setReimbursements] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [selectedReimbursementId, setSelectedReimbursementId] = useState(null);
  const [submittingRejection, setSubmittingRejection] = useState(false);

  // Bulk selection state
  const [selectedReimbursements, setSelectedReimbursements] = useState(
    new Set(),
  );
  const [showBulkRejectPopup, setShowBulkRejectPopup] = useState(false);

  // Role toggle states
  const [selectedViewRole, setSelectedViewRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    if (!user) return;

    const roles = [];
    if (user.authorities?.includes("ROLE_MANAGER")) roles.push("MANAGER");
    if (user.authorities?.includes("ROLE_HR")) roles.push("HR");

    setAvailableRoles(roles);

    // Default role: MANAGER > HR
    if (roles.includes("MANAGER")) {
      setSelectedViewRole("MANAGER");
    } else if (roles.includes("HR")) {
      setSelectedViewRole("HR");
    }
  }, [user]);

  const fetchReimbursements = async (roleOverride = null) => {
    const roleToUse = roleOverride || selectedViewRole;
    if (!roleToUse) return;

    setLoading(true);
    try {
      const endpoint =
        roleToUse === "MANAGER"
          ? `${process.env.REACT_APP_API_MANAGER_URL}/reimbursements/requested`
          : `${process.env.REACT_APP_API_HR_URL}/reimbursements/requested`;

      const response = await axiosInstance.get(endpoint);
      setReimbursements(response.data.data);
      setSelectedReimbursements(new Set());
    } catch (error) {
      toast.error("Failed to fetch reimbursements");
    } finally {
      setLoading(false);
    }
  };

  // Role switching handler
  const handleRoleSwitch = async (role) => {
    if (role === selectedViewRole) return;

    setSelectedViewRole(role);
    setShowReport(false); // Reset report view when switching roles
    setSelectedReimbursements(new Set()); // Clear selections
  };

  useEffect(() => {
    if (!selectedViewRole) return; // Don't fetch until role is determined

    fetchReimbursements();
  }, [selectedViewRole]);

  const toggleReport = () => {
    if (showReport) {
      setShowReport(false);
    } else {
      setLoading(true);

      let endpoint;
      switch (selectedViewRole) {
        case "MANAGER":
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/reimbursements/all`;
          break;
        case "HR":
          endpoint = `${process.env.REACT_APP_API_HR_URL}/reimbursements/all`;
          break;
        default:
          endpoint = `${process.env.REACT_APP_API_HR_URL}/reimbursements/all`;
      }

      axiosInstance
        .get(endpoint)
        .then((response) => {
          setReportData(response.data.data);
          setShowReport(true);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching report data:", error);
          toast.error("Failed to fetch report data");
          setLoading(false);
        });
    }
  };

  // Bulk selection handlers
  const handleToggleSelect = (reimbursementId) => {
    setSelectedReimbursements((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reimbursementId)) {
        newSet.delete(reimbursementId);
      } else {
        newSet.add(reimbursementId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const pendingReimbursements = getPendingReimbursements();
    const selectableReimbursements = pendingReimbursements.filter((item) => {
      if (selectedViewRole === "MANAGER") {
        return item.managerApprovalStatus === "Requested";
      } else if (selectedViewRole === "HR") {
        return item.hrApprovalStatus === "Requested";
      }
      return false;
    });

    if (selectedReimbursements.size === selectableReimbursements.length) {
      setSelectedReimbursements(new Set());
    } else {
      setSelectedReimbursements(
        new Set(selectableReimbursements.map((item) => item.reimbursementId)),
      );
    }
  };

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedReimbursements.size === 0) {
      toast.warning("Please select at least one reimbursement");
      return;
    }

    const payload = Array.from(selectedReimbursements);

    const endpoint =
      selectedViewRole === "MANAGER"
        ? `${process.env.REACT_APP_API_MANAGER_URL}/reimbursements/approve/bulk`
        : `${process.env.REACT_APP_API_HR_URL}/reimbursements/approve/bulk`;

    setLoading(true);
    try {
      await axiosInstance.put(endpoint, payload);

      toast.success(`Approved ${payload.length} reimbursement(s)`);

      setReimbursements((prev) =>
        prev.map((item) =>
          selectedReimbursements.has(item.reimbursementId)
            ? { ...item, managerApprovalStatus: "APPROVED" }
            : item,
        ),
      );

      setSelectedReimbursements(new Set());
      onRequestProcessed?.();
    } catch {
      toast.error("Bulk reimbursement approval failed");
    } finally {
      setLoading(false);
    }
  };

  // Bulk reject
  const handleBulkRejectClick = () => {
    if (selectedReimbursements.size === 0) {
      toast.warning("Please select at least one reimbursement");
      return;
    }
    setShowBulkRejectPopup(true);
  };

  const handleBulkRejectSubmit = async (rejectionMessage) => {
    if (!rejectionMessage.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    const payload = Array.from(selectedReimbursements).map((id) => ({
      reimbursementHistoryId: id,
      remarks: rejectionMessage.trim(),
    }));

    const endpoint =
      selectedViewRole === "MANAGER"
        ? `${process.env.REACT_APP_API_MANAGER_URL}/reimbursements/reject/bulk`
        : `${process.env.REACT_APP_API_HR_URL}/reimbursements/reject/bulk`;

    setSubmittingRejection(true);
    try {
      await axiosInstance.put(endpoint, payload);

      toast.success(`Rejected ${payload.length} reimbursement(s)`);

      setReimbursements((prev) =>
        prev.map((item) =>
          selectedReimbursements.has(item.reimbursementId)
            ? { ...item, managerApprovalStatus: "Rejected" }
            : item,
        ),
      );

      setSelectedReimbursements(new Set());
      setShowBulkRejectPopup(false);
      onRequestProcessed?.();
    } catch {
      toast.error("Bulk reimbursement rejection failed");
    } finally {
      setSubmittingRejection(false);
    }
  };

  // File handling functions
  const handleViewFile = (file, fileName, fileType) => {
    try {
      const byteCharacters = atob(file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileType });

      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, "_blank");

      setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
    } catch (error) {
      console.error("Error viewing file:", error);
      toast.error("Failed to open file");
    }
  };

  const handleDownloadFile = (file, fileName, fileType) => {
    try {
      const byteCharacters = atob(file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileType });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName || "reimbursement-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
      toast.success("File downloaded successfully");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes("pdf")) return "📄";
    if (fileType?.includes("image")) return "🖼️";
    if (fileType?.includes("word") || fileType?.includes("document"))
      return "📝";
    if (fileType?.includes("excel") || fileType?.includes("spreadsheet"))
      return "📊";
    return "📎";
  };

  const RejectReimbursementPopup = ({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false,
    isBulk = false,
    count = 1,
  }) => {
    const [rejectionMessage, setRejectionMessage] = useState("");

    useEffect(() => {
      if (isOpen) {
        setRejectionMessage("");
      }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!rejectionMessage.trim()) {
        toast.error("Please provide a reason for rejection");
        return;
      }
      onSubmit(rejectionMessage);
    };

    const handleBackdropClick = (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    return (
      <div className="reject-popup-overlay" onClick={handleBackdropClick}>
        <div className="reject-popup" onClick={(e) => e.stopPropagation()}>
          <div className="reject-popup-header">
            <h2 className="reject-popup-title">
              <i className="reject-popup-icon">⚠︎</i>
              Reject Reimbursement{isBulk && count > 1 ? "s" : ""}
            </h2>
            <button
              className="reject-popup-close"
              onClick={onClose}
              disabled={isSubmitting}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="reject-popup-form">
            <div className="reject-popup-content">
              <p className="reject-popup-message">
                {isBulk
                  ? `Please provide a reason for rejecting ${count} reimbursement${count > 1 ? "s" : ""}. This will help the employees understand the decision.`
                  : "Please provide a reason for rejecting this reimbursement request. This will help the employee understand the decision."}
              </p>

              <div className="reject-popup-input-group">
                <label
                  htmlFor="rejectionMessage"
                  className="reject-popup-label"
                >
                  Rejection Reason <span className="required">*</span>
                </label>
                <textarea
                  id="rejectionMessage"
                  className="reject-popup-textarea"
                  placeholder="Enter the reason for rejection..."
                  value={rejectionMessage}
                  onChange={(e) => setRejectionMessage(e.target.value)}
                  rows={4}
                  maxLength={500}
                  disabled={isSubmitting}
                  required
                />
                <div className="reject-popup-char-count">
                  {rejectionMessage.length}/500 characters
                </div>
              </div>
            </div>

            <div className="reject-popup-actions">
              <button
                type="button"
                className="reject-popup-button cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="reject-popup-button reject"
                disabled={isSubmitting || !rejectionMessage.trim()}
              >
                {isSubmitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Rejecting...
                  </>
                ) : (
                  `Reject Reimbursement${isBulk && count > 1 ? "s" : ""}`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleApprove = (id) => {
    let endpoint;
    switch (selectedViewRole) {
      case "MANAGER":
        endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/reimbursements/approve/${id}`;
        break;
      case "HR":
        endpoint = `${process.env.REACT_APP_API_HR_URL}/reimbursements/approve/${id}`;
        break;
      default:
        endpoint = `${process.env.REACT_APP_API_HR_URL}/reimbursements/approve/${id}`;
    }

    setLoading(true);
    axiosInstance
      .put(endpoint)
      .then(() => {
        toast.success("Reimbursement approved successfully");
        if (onRequestProcessed) {
          onRequestProcessed();
        }
        setReimbursements((prevState) =>
          prevState.map((item) =>
            item.reimbursementId === id
              ? { ...item, managerApprovalStatus: "APPROVED" }
              : item,
          ),
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error approving reimbursement:", error);
        toast.error("Failed to approve reimbursement");
        setLoading(false);
      });
  };

  const handleRejectClick = (id) => {
    setSelectedReimbursementId(id);
    setShowRejectPopup(true);
  };

  const handleRejectSubmit = (rejectionMessage) => {
    let endpoint;
    switch (selectedViewRole) {
      case "MANAGER":
        endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/reimbursements/reject/${selectedReimbursementId}`;
        break;
      case "HR":
        endpoint = `${process.env.REACT_APP_API_HR_URL}/reimbursements/reject/${selectedReimbursementId}`;
        break;
      default:
        endpoint = `${process.env.REACT_APP_API_HR_URL}/reimbursements/reject/${selectedReimbursementId}`;
    }

    setSubmittingRejection(true);
    axiosInstance
      .put(endpoint, rejectionMessage.trim(), {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        toast.success("Reimbursement rejected successfully");
        if (onRequestProcessed) {
          onRequestProcessed();
        }
        setReimbursements((prevState) =>
          prevState.map((item) =>
            item.reimbursementId === selectedReimbursementId
              ? { ...item, managerApprovalStatus: "Rejected" }
              : item,
          ),
        );
        handleCloseRejectPopup();
      })
      .catch((error) => {
        console.error("Error rejecting reimbursement:", error);
        toast.error("Failed to reject reimbursement");
      })
      .finally(() => {
        setSubmittingRejection(false);
      });
  };

  const handleCloseRejectPopup = () => {
    setShowRejectPopup(false);
    setSelectedReimbursementId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  const getStatusBadge = (status) => {
    const statusClass = status
      ? status.toLowerCase().replace("_", "-")
      : "unknown";
    return (
      <span className={`status-badge status-${statusClass}`}>
        {status || "N/A"}
      </span>
    );
  };

  const renderFileAttachment = (file, fileName, fileType) => {
    if (!file || !fileName) {
      return <span className="no-file">No attachment</span>;
    }

    return (
      <div className="attachment">
        <div className="file-actions">
          <div className="file-info">
            <span className="file-icon">{getFileIcon(fileType)}</span>
            <span className="file-name" title={fileName}>
              {fileName.length > 15
                ? `${fileName.substring(0, 12)}...`
                : fileName}
            </span>
          </div>
          <div className="file-buttons">
            <button
              className="file-action-btn view-btn"
              onClick={() => handleViewFile(file, fileName, fileType)}
              title="View file"
            >
              👁️
            </button>
            <button
              className="file-action-btn download-btn"
              onClick={() => handleDownloadFile(file, fileName, fileType)}
              title="Download file"
            >
              ⬇️
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderActionButtons = (reimbursement) => {
    let showActions = false;
    if (selectedViewRole === "MANAGER") {
      showActions = reimbursement.managerApprovalStatus === "Requested";
    } else if (selectedViewRole === "HR") {
      showActions = reimbursement.hrApprovalStatus === "Requested";
    }

    if (showActions) {
      return (
        <div className="action-buttons">
          <IconButton
            className="icon-button--add"
            icon="mdi:check"
            onClick={() => handleApprove(reimbursement.reimbursementId)}
            disabled={loading}
          />
          <IconButton
            className="icon-button--delete"
            icon="material-symbols-light:close"
            onClick={() => handleRejectClick(reimbursement.reimbursementId)}
            disabled={loading}
          />
        </div>
      );
    }
    return <span className="no-actions">No actions available</span>;
  };

  // Render employee name with checkbox integration
  const renderEmployeeNameWithCheckbox = (item) => {
    let isSelectable = false;
    if (selectedViewRole === "MANAGER") {
      isSelectable = item.managerApprovalStatus === "Requested";
    } else if (selectedViewRole === "HR") {
      isSelectable = item.hrApprovalStatus === "Requested";
    }

    const isChecked = selectedReimbursements.has(item.reimbursementId);

    return (
      <div className="employee-name-cell">
        {isSelectable && (
          <label className="custom-checkbox">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => handleToggleSelect(item.reimbursementId)}
            />
            <span className="checkmark"></span>
          </label>
        )}
        <span className={`employee-name ${isSelectable ? "selectable" : ""}`}>
          {item.employeeName}
        </span>
      </div>
    );
  };

  // Transform data for pending reimbursements table
  const transformPendingData = (data) => {
    return [...data]
      .sort((a, b) => new Date(b.requestedOn) - new Date(a.requestedOn))
      .map((item) => ({
        employeeName: renderEmployeeNameWithCheckbox(item),
        reimbursementType: item.reimbursementType || "—",
        description: item.description,
        amount: formatAmount(item.amount),
        requestedOn: formatDate(item.requestedOn),
        attachment: renderFileAttachment(
          item.file,
          item.fileName,
          item.fileType,
        ),
        managerStatus: getStatusBadge(item.managerApprovalStatus),
        hrStatus: getStatusBadge(item.hrApprovalStatus),
        paymentStatus: getStatusBadge(item.paymentStatus),
        actions: renderActionButtons(item),
      }));
  };

  // Transform data for report table
  const transformReportData = (data) => {
    return [...data]
      .sort((a, b) => new Date(b.requestedOn) - new Date(a.requestedOn))
      .map((item) => ({
        employeeName: item.employeeName,
        reimbursementType: item.reimbursementType || "—",
        description: item.description,
        amount: formatAmount(item.amount),
        requestedOn: formatDate(item.requestedOn),
        attachment: renderFileAttachment(
          item.file,
          item.fileName,
          item.fileType,
        ),
        managerStatus: getStatusBadge(item.managerApprovalStatus),
        hrStatus: getStatusBadge(item.hrApprovalStatus),
        paymentStatus: getStatusBadge(item.paymentStatus),
      }));
  };

  // Columns for pending reimbursements
  const pendingColumns = [
    { header: "Employee Name", accessor: "employeeName" },
    { header: "Type", accessor: "reimbursementType" },
    { header: "Description", accessor: "description" },
    { header: "Amount", accessor: "amount" },
    { header: "Requested On", accessor: "requestedOn" },
    { header: "Attachment", accessor: "attachment" },
    { header: "Manager Status", accessor: "managerStatus" },
    { header: "HR Status", accessor: "hrStatus" },
    { header: "Payment Status", accessor: "paymentStatus" },
    { header: "Actions", accessor: "actions" },
  ];

  // Columns for report table (no actions column)
  const reportColumns = [
    { header: "Employee Name", accessor: "employeeName" },
    { header: "Type", accessor: "reimbursementType" },
    { header: "Description", accessor: "description" },
    { header: "Amount", accessor: "amount" },
    { header: "Requested On", accessor: "requestedOn" },
    { header: "Attachment", accessor: "attachment" },
    { header: "Manager Status", accessor: "managerStatus" },
    { header: "HR Status", accessor: "hrStatus" },
    { header: "Payment Status", accessor: "paymentStatus" },
  ];

  // Filter reimbursements based on role and approval status
  const getPendingReimbursements = () => {
    return reimbursements.filter((item) => {
      if (selectedViewRole === "MANAGER") {
        // In Manager view, show only items that are not approved by manager
        return item.managerApprovalStatus === "Requested";
      } else if (selectedViewRole === "HR") {
        // In HR view, show only items that are approved by manager but not by HR
        // Handle both "APPROVED" and "Approved" due to inconsistent API response casing
        return (
          (item.managerApprovalStatus === "APPROVED" ||
            item.managerApprovalStatus === "Approved") &&
          item.hrApprovalStatus === "Requested"
        );
      }
      return true;
    });
  };

  const pendingReimbursements = getPendingReimbursements();

  // Count of reimbursements available for selection
  const requestedReimbursementsCount = pendingReimbursements.filter(
    (item) =>
      (selectedViewRole === "MANAGER" &&
        item.managerApprovalStatus === "Requested") ||
      (selectedViewRole === "HR" && item.hrApprovalStatus === "Requested"),
  ).length;

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="team-reimbursement">
      {/* Bulk Action Controls - only show when items are selected */}
      {!showReport && selectedReimbursements.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-left">
            <button onClick={handleSelectAll} className="select-all-btn">
              <span className="btn-icon">
                {selectedReimbursements.size === requestedReimbursementsCount
                  ? "☑"
                  : "☐"}
              </span>
              {selectedReimbursements.size === requestedReimbursementsCount
                ? "Deselect All"
                : "Select All"}
            </button>

            {selectedReimbursements.size > 0 && (
              <div className="selection-count">
                <span className="count-badge">
                  {selectedReimbursements.size}
                </span>
                <span className="count-text">
                  {selectedReimbursements.size === 1
                    ? "item selected"
                    : "items selected"}
                </span>
              </div>
            )}
          </div>

          {selectedReimbursements.size > 0 && (
            <div className="bulk-actions-right">
              <button
                className="bulk-action-btn approve-btn"
                onClick={handleBulkApprove}
                disabled={loading}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Approve ({selectedReimbursements.size})</span>
              </button>

              <button
                className="bulk-action-btn reject-btn"
                onClick={handleBulkRejectClick}
                disabled={loading}
              >
                <svg
                  className="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span>Reject ({selectedReimbursements.size})</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pending Reimbursements Table */}
      {!showReport && (
        <div className="reimbursement-section">
          <GeneralTable
            columns={pendingColumns}
            data={transformPendingData(pendingReimbursements)}
            title="Pending Approval Requests"
            titleActions={
              <div className="team-page-toolbar-actions">
                {availableRoles.length > 1 && availableRoles.map((role) => (
                  <button
                    key={role}
                    className={`role-toggle-btn ${selectedViewRole === role ? "active" : ""}`}
                    onClick={() => handleRoleSwitch(role)}
                    disabled={loading}
                  >
                    {role === "MANAGER" ? "Manager" : "HR"}
                  </button>
                ))}
                <button
                  onClick={toggleReport}
                  className={`team-reimbursement-togglebutton ${showReport ? "active" : "inactive"}`}
                  disabled={loading}
                >
                  <i className="icon-report"></i>
                  {showReport ? "Close report" : "Full report"}
                  {loading && <span className="loading-spinner"></span>}
                </button>
              </div>
            }
          />
        </div>
      )}

      {/* Report Table */}
      {showReport && (
        <div className="reimbursement-section">
          <GeneralTable
            columns={reportColumns}
            data={transformReportData(reportData)}
            title="Complete Reimbursement Report"
            titleActions={
              <div className="team-page-toolbar-actions">
                {availableRoles.length > 1 && availableRoles.map((role) => (
                  <button
                    key={role}
                    className={`role-toggle-btn ${selectedViewRole === role ? "active" : ""}`}
                    onClick={() => handleRoleSwitch(role)}
                    disabled={loading}
                  >
                    {role === "MANAGER" ? "Manager" : "HR"}
                  </button>
                ))}
                <button
                  onClick={toggleReport}
                  className={`team-reimbursement-togglebutton ${showReport ? "active" : "inactive"}`}
                  disabled={loading}
                >
                  <i className="icon-report"></i>
                  {showReport ? "Close report" : "Full report"}
                  {loading && <span className="loading-spinner"></span>}
                </button>
              </div>
            }
          />
        </div>
      )}

      {/* Single Reject Reimbursement Popup */}
      <RejectReimbursementPopup
        isOpen={showRejectPopup}
        onClose={handleCloseRejectPopup}
        onSubmit={handleRejectSubmit}
        isSubmitting={submittingRejection}
        isBulk={false}
      />

      {/* Bulk Reject Reimbursement Popup */}
      <RejectReimbursementPopup
        isOpen={showBulkRejectPopup}
        onClose={() => setShowBulkRejectPopup(false)}
        onSubmit={handleBulkRejectSubmit}
        isSubmitting={submittingRejection}
        isBulk={true}
        count={selectedReimbursements.size}
      />
    </div>
  );
}

export default TeamReimbursements;
